import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Plus, Trash2, ArrowRight, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE as API } from '../../config/api';

const AFFIX_TYPES = [
  { value: 'prefix', label: 'Prefix' },
  { value: 'suffix', label: 'Suffix' },
  { value: 'infix', label: 'Infix' },
  { value: 'circumfix', label: 'Circumfix' },
];

const POS_OPTIONS = ['noun', 'verb', 'adjective', 'adverb', 'pronoun', 'preposition', 'conjunction', 'any'];

const DerivationPage = () => {
  const { conlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  const [affixes, setAffixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [affix, setAffix] = useState('');
  const [type, setType] = useState('suffix');
  const [meaning, setMeaning] = useState('');
  const [posFrom, setPosFrom] = useState('');
  const [posTo, setPosTo] = useState('');
  const [examples, setExamples] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => { fetchData(); }, [conlangId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/affixes`, { withCredentials: true });
      setAffixes(res.data);
    } catch { toast.error('Failed to load affixes'); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setAffix(''); setType('suffix'); setMeaning(''); setPosFrom(''); setPosTo(''); setExamples(''); setNotes(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const res = await axios.post(`${API}/conlangs/${conlangId}/affixes`, {
        affix, type, meaning,
        part_of_speech_from: posFrom || null,
        part_of_speech_to: posTo || null,
        examples: examples ? examples.split('\n').filter(s => s.trim()) : [],
        notes: notes || null,
      }, { withCredentials: true });
      setAffixes([...affixes, res.data]);
      setDialogOpen(false);
      resetForm();
      toast.success('Affix added');
    } catch { toast.error('Failed to add affix'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this affix?')) return;
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/affixes/${id}`, { withCredentials: true });
      setAffixes(affixes.filter(a => a.affix_id !== id));
      toast.success('Affix deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const formatAffix = (a) => {
    if (a.type === 'prefix') return `${a.affix}-`;
    if (a.type === 'suffix') return `-${a.affix}`;
    if (a.type === 'infix') return `-${a.affix}-`;
    return a.affix;
  };

  const getTypeColor = (t) => {
    const colors = { prefix: 'text-secondary border-secondary/50', suffix: 'text-primary border-primary/50', infix: 'text-tertiary border-tertiary/50', circumfix: 'text-quaternary border-quaternary/50' };
    return colors[t] || 'text-foreground-muted border-border';
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="derivation-title">Derivation</h2>
          <p className="text-foreground-muted mt-1">Define derivational affixes for {conlang?.name}</p>
        </div>
        {canEdit && (
        <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary/90" data-testid="add-affix-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Affix
        </Button>
        )}
      </div>

      {affixes.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4" data-testid="affixes-list">
          {affixes.map(a => (
            <Card key={a.affix_id} className="bg-background-paper border-border">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-conlang text-xl text-foreground">{formatAffix(a)}</span>
                      <Badge variant="outline" className={getTypeColor(a.type)}>{a.type}</Badge>
                    </div>
                    <p className="text-foreground-muted text-sm mb-2">{a.meaning}</p>
                    {(a.part_of_speech_from || a.part_of_speech_to) && (
                      <div className="flex items-center gap-2 text-xs text-foreground-subtle">
                        {a.part_of_speech_from && <span>{a.part_of_speech_from}</span>}
                        {a.part_of_speech_from && a.part_of_speech_to && <ArrowRight className="w-3 h-3" />}
                        {a.part_of_speech_to && <span>{a.part_of_speech_to}</span>}
                      </div>
                    )}
                    {a.examples?.length > 0 && <div className="mt-2 text-xs text-foreground-subtle italic">{a.examples.slice(0, 2).join(', ')}</div>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(a.affix_id)} className="text-destructive hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-background-paper border-border border-dashed">
          <CardContent className="p-12 text-center">
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">No derivational affixes yet</h3>
            <p className="text-foreground-muted mb-4">Add prefixes, suffixes, and other affixes that create new words.</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-2" /> Add Affix</Button>
          </CardContent>
        </Card>
      )}

      <div className="p-4 rounded-md bg-primary/10 border border-primary/30 flex gap-3">
        <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-primary text-sm">
          <strong>Derivation</strong> creates new words from existing ones by adding affixes. For example, "happy" + "-ness" = "happiness" (adjective to noun), or "un-" + "do" = "undo" (reversing meaning).
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg" data-testid="affix-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Add Affix</DialogTitle>
            <DialogDescription className="text-foreground-muted">Define a derivational affix</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Affix</Label>
                <Input value={affix} onChange={(e) => setAffix(e.target.value)} placeholder="e.g., -tion, un-" className="bg-background border-border font-mono" required data-testid="affix-input" />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="bg-background border-border" data-testid="affix-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {AFFIX_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Meaning</Label>
              <Input value={meaning} onChange={(e) => setMeaning(e.target.value)} placeholder="What this affix does..." className="bg-background border-border" required data-testid="affix-meaning-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Attaches to (POS)</Label>
                <Select value={posFrom} onValueChange={setPosFrom}>
                  <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {POS_OPTIONS.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Creates (POS)</Label>
                <Select value={posTo} onValueChange={setPosTo}>
                  <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Same" /></SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {POS_OPTIONS.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Examples (one per line)</Label>
              <Textarea value={examples} onChange={(e) => setExamples(e.target.value)} placeholder="root + affix = result" className="bg-background border-border min-h-[60px]" data-testid="affix-examples" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !affix || !meaning} data-testid="submit-affix">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Affix'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DerivationPage;
